import { Observable } from "rxjs/internal/Observable";
import { catchError, map } from "rxjs/operators";
import { API_URL } from "../../enums/api.enums";
import HttpClient from "../http-client";
import Utils from "../../utils/base-utils";
import { ICreateBookReq } from "../../common/book.interface";


export default class BookApi {
    static apiURL = API_URL;

    // GET /books - Lấy danh sách sách với tìm kiếm và phân trang
    static getListBook(params?: {
        search?: string;
        categoryIds?: number[];
        page?: number;
        limit?: number;
    }): Observable<any> {
        let queryParam = '';
        if (params) {
            const queryParams = new URLSearchParams();
            if (params.search) queryParams.append('search', params.search);
            if (params.categoryIds && params.categoryIds.length > 0) {
                queryParams.append('categoryIds', JSON.stringify(params.categoryIds));
            }
            if (params.page) queryParams.append('page', params.page.toString());
            if (params.limit) queryParams.append('limit', params.limit.toString());
            queryParam = queryParams.toString() ? `?${queryParams.toString()}` : '';
        }

        const api = `${BookApi.apiURL.HOST}/${this.apiURL.GET_LIST_BOOK}${queryParam}`;
        return HttpClient.get(api).pipe(
            map(
                (res) => (res as any) || null,
                catchError((error) => new Observable())
            )
        );
    }

    // GET /books/all - Lấy tất cả sách (không phân trang)
    static getAllBooks(): Observable<any> {
        const api = `${BookApi.apiURL.HOST}/${this.apiURL.GET_LIST_BOOK}/all`;
        return HttpClient.get(api).pipe(
            map(
                (res) => (res as any) || null,
                catchError((error) => new Observable())
            )
        );
    }

    // GET /books/count - Đếm số lượng sách
    static getTotalBook(): Observable<any> {
        const api = `${BookApi.apiURL.HOST}/${this.apiURL.GET_TOTAL_BOOK}`;
        return HttpClient.get(api).pipe(
            map(
                (res) => (res as any) || null,
                catchError((error) => new Observable())
            )
        );
    }

    // GET /books/:id - Lấy chi tiết sách
    static getDetailBook(id: number | string): Observable<any> {
        const api = `${BookApi.apiURL.HOST}/${this.apiURL.DETAIL_BOOK}/${id}`;
        return HttpClient.get(api).pipe(
            map(
                (res) => (res as any) || null,
                catchError((error) => new Observable())
            )
        );
    }

    // POST /books - Tạo sách mới
    static addBook(data: ICreateBookReq): Observable<any> {
        const api = `${BookApi.apiURL.HOST}/${this.apiURL.CREATE_BOOK}`;

        // Sử dụng trực tiếp data đã được format đúng từ component
        const requestBody = {
            name: data.name,                           // Bắt buộc
            thumbnailUrl: data.thumbnailUrl || '',     // Empty string thay vì undefined
            ISBN: data.ISBN || '',                     // Empty string thay vì undefined
            description: data.description || '',       // Empty string thay vì undefined
            country: data.country || 'Vietnam',        // Default value
            language: data.language || 'vietnamese',   // Default value
            pageNumber: data.pageNumber,               // Có thể null
            ebookUrl: data.ebookUrl || 'string',            // Empty string thay vì undefined
            previewEbookUrl: data.previewEbookUrl || 'string', // Empty string thay vì undefined
            previewAudiobookUrl: data.previewAudiobookUrl || '', // Empty string thay vì undefined
            pricePaper: data.pricePaper || 0,         // Default 0
            priceEbook: data.priceEbook || 0,         // Default 0
            priceAudio: data.priceAudio || 0,         // Default 0
            amount: data.amount || 50,                // Default 50
            releaseDate: data.releaseDate,            // Đã được convert sang ISO-8601 format
            generateId: data.generateId || '',        // Empty string thay vì undefined
            publishing_house_id: data.publishing_house_id, // Có thể null
            authorIds: data.authorIds || [],          // Empty array thay vì undefined
            translatorIds: data.translatorIds || [],  // Empty array thay vì undefined
            categoryIds: data.categoryIds || [],      // Empty array thay vì undefined
            size: data.size || '',                    // Empty string thay vì undefined
            sync: data.sync !== undefined ? data.sync : true, // Default true
        };

        console.log('Creating book:', requestBody);
        return HttpClient.post(api, requestBody).pipe(
            map(
                (res) => (res as any) || null,
                catchError((error) => new Observable())
            )
        );
    }

    // PUT /books/:id - Cập nhật sách
    static editBook(id: number | string, data: any): Observable<any> {
        const api = `${BookApi.apiURL.HOST}/${this.apiURL.EDIT_BOOK}/${id}`;

        // Map dữ liệu theo API guide
        const requestBody: any = {};

        if (data.name || data.title) requestBody.name = data.name || data.title;
        if (data.description !== undefined) requestBody.description = data.description;
        if (data.ISBN) requestBody.ISBN = data.ISBN;
        if (data.thumbnailUrl) requestBody.thumbnailUrl = data.thumbnailUrl;
        if (data.pricePaper !== undefined || data.price !== undefined) requestBody.pricePaper = data.pricePaper ?? data.price;
        if (data.priceEbook !== undefined) requestBody.priceEbook = data.priceEbook;
        if (data.priceAudio !== undefined) requestBody.priceAudio = data.priceAudio;
        if (data.amount !== undefined) requestBody.amount = data.amount;
        if (data.language !== undefined) requestBody.language = data.language;
        if (data.pageNumber !== undefined) requestBody.pageNumber = data.pageNumber;
        if (data.releaseDate !== undefined) requestBody.releaseDate = data.releaseDate;
        if (data.size !== undefined) requestBody.size = data.size;
        if (data.isHaveEbook !== undefined) requestBody.isHaveEbook = data.isHaveEbook;
        if (data.isHaveAudio !== undefined) requestBody.isHaveAudio = data.isHaveAudio;
        if (data.publishing_house_id !== undefined || data.publishingHouseId !== undefined || data.publisherId !== undefined) {
            requestBody.publishing_house_id = data.publishing_house_id ?? data.publishingHouseId ?? data.publisherId;
        }
        if (data.categoryIds || data.categoriesId) {
            requestBody.categoryIds = data.categoryIds || data.categoriesId;
        }
        if (data.authorIds || data.authorsId) {
            requestBody.authorIds = data.authorIds || data.authorsId;
        }

        console.log('Updating book:', requestBody);
        return HttpClient.put(api, requestBody).pipe(
            map((res) => (res as any) || null),
            catchError((error) => {
                console.error('Update book failed:', {
                    status: error?.response?.status,
                    data: error?.response?.data,
                    message: error?.response?.data?.message || error?.message || 'Cập nhật sách thất bại',
                    payload: requestBody,
                });
                throw error;
            })
        );
    }

    // DELETE /books/:id - Xóa sách (Soft Delete)
    static deleteBook(id: number | string): Observable<any> {
        const api = `${BookApi.apiURL.HOST}/${this.apiURL.DELETE_BOOK}/${id}`;
        return HttpClient.delete(api).pipe(
            map(
                (res) => (res as any) || null,
                catchError((error) => new Observable())
            )
        );
    }

    static getBookBestSeller(body: any): Observable<any> {
        const queryParam = Utils.parseObjectToQueryParameter(body);
        const api = `${BookApi.apiURL.HOST}/${this.apiURL.TOP_BOOK_BESTSELLER}${queryParam}`;
        return HttpClient.get(api).pipe(
            map(
                (res) => (res as any) || null,
                catchError((error) => new Observable())
            )
        );
    }

    // POST /books/:id/assign-tags - Gán tags cho sách
    static assignTags(bookId: number | string, tagIds: number[]): Observable<any> {
        const api = `${BookApi.apiURL.HOST}/${this.apiURL.GET_LIST_BOOK}/${bookId}/assign-tags`;
        return HttpClient.post(api, { tagIds }).pipe(
            map(
                (res) => (res as any) || null,
                catchError((error) => new Observable())
            )
        );
    }

    // GET /books/:id/tags - Lấy tags của sách
    static getBookTags(bookId: number | string): Observable<any> {
        const api = `${BookApi.apiURL.HOST}/${this.apiURL.GET_LIST_BOOK}/${bookId}/tags`;
        return HttpClient.get(api).pipe(
            map(
                (res) => (res as any) || null,
                catchError((error) => new Observable())
            )
        );
    }

    // POST /books/upload-epub - Upload file EPUB
    static uploadEpub(file: File, bookId: number): Observable<any> {
        const formData = new FormData();
        formData.append('epubFile', file);
        formData.append('bookId', bookId.toString());

        const api = `${BookApi.apiURL.HOST}/${this.apiURL.GET_LIST_BOOK}/upload-epub`;
        return HttpClient.post(api, formData).pipe(
            map(
                (res) => (res as any) || null,
                catchError((error) => new Observable())
            )
        );
    }

    // POST /books/upload-preview - Upload file preview
    static uploadPreview(file: File, bookId: number): Observable<any> {
        const formData = new FormData();
        formData.append('previewEbook', file);
        formData.append('bookId', bookId.toString());

        const api = `${BookApi.apiURL.HOST}/${this.apiURL.GET_LIST_BOOK}/upload-preview`;
        return HttpClient.post(api, formData).pipe(
            map(
                (res) => (res as any) || null,
                catchError((error) => new Observable())
            )
        );
    }

    // GET /books/epub/:id - Download EPUB file
    static downloadEpub(bookId: number | string): Observable<any> {
        const api = `${BookApi.apiURL.HOST}/${this.apiURL.GET_LIST_BOOK}/epub/${bookId}`;
        return HttpClient.get(api, { responseType: 'blob' }).pipe(
            map(
                (res) => (res as any) || null,
                catchError((error) => new Observable())
            )
        );
    }

    // GET /books/preview-images - Lấy ảnh preview
    static getPreviewImages(bookId: number | string): Observable<any> {
        const api = `${BookApi.apiURL.HOST}/${this.apiURL.GET_LIST_BOOK}/preview-images?id=${bookId}`;
        return HttpClient.get(api, { responseType: 'blob' }).pipe(
            map(
                (res) => (res as any) || null,
                catchError((error) => new Observable())
            )
        );
    }

    // GET /books/mp3 - Stream audio MP3
    static streamAudio(bookId: number, chapter: number, voice: 'male' | 'female'): string {
        return `${BookApi.apiURL.HOST}/${this.apiURL.GET_LIST_BOOK}/mp3?id=${bookId}&chapter=${chapter}&voice=${voice}`;
    }

    // GET /books/summaryMp3 - Stream summary audio
    static streamSummaryAudio(bookId: number, voice: 'male' | 'female'): string {
        return `${BookApi.apiURL.HOST}/${this.apiURL.GET_LIST_BOOK}/summaryMp3?id=${bookId}&voice=${voice}`;
    }

    // GET /books/transcript - Lấy transcript
    static getTranscript(bookId: number, chapter: number, voice: 'male' | 'female', type: 'srt' | 'vtt' = 'srt'): Observable<any> {
        const api = `${BookApi.apiURL.HOST}/${this.apiURL.GET_LIST_BOOK}/transcript?id=${bookId}&chapter=${chapter}&voice=${voice}&type=${type}`;
        return HttpClient.get(api).pipe(
            map(
                (res) => (res as any) || null,
                catchError((error) => new Observable())
            )
        );
    }

    // GET /books/generate-audio/:id - Generate audio
    static generateAudio(bookId: number | string): Observable<any> {
        const api = `${BookApi.apiURL.HOST}/${this.apiURL.GET_LIST_BOOK}/generate-audio/${bookId}`;
        return HttpClient.get(api).pipe(
            map(
                (res) => (res as any) || null,
                catchError((error) => new Observable())
            )
        );
    }

}